import { useMemo, useState, useCallback } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  BookOpen,
  Clock,
  User,
  Layers,
  Play,
  CheckCircle2,
  Award,
  Loader2,
  ChevronRight,
  ClipboardCheck,
  Lock,
  PartyPopper,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import {
  useCourse,
  useEnrollInCourse,
  useCourseEnrollment,
  useLessonProgress,
  useMarkLessonStarted,
  useCoursePrefetch,
  useInProgressCoursesPrefetch,
  useBestAssessmentScore,
  useIsSuperAdmin,
  useHasContentEntitlement,
} from '@/api/hooks';
import { ModuleAccordion, ProgressRing } from '@/components/learning';
import { computeUnlockedModules } from '@/lib/course-drip';
import { useDripSeries } from '@/api/course-drip-series';
import { useMyLinkCourseId } from '@/api/link-courses';
import type { Lesson, Module } from '@/api/client';
import type { Asset } from '@/components/assets/AssetCard';
import { AssetDetailPanel } from '@/components/assets/AssetDetailPanel';
import { hasAssetSource } from '@/components/assets/asset-source';
import { CourseAssetsDrawer } from '@/components/course-assets/CourseAssetsDrawer';
import { useDrawerHistory } from '@/components/course-assets/useDrawerHistory';
import { NavIconLibrary } from '@/components/icons/NavIcons';
import { useTrackEntityView } from '@/hooks/use-entity-tracker';
import { resolveMediaUrl } from '@/lib/media';
import { MediaImage } from '@/components/media';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Celebration icon + accent colour for a best assessment score, on a sliding
 * scale. Per design-system §H, FB accents fire on celebration only — so the
 * top tiers use fb-lime / fb-amber (inline, since FB accents aren't Tailwind
 * utilities) and a passing score uses the success token. Below pass → none.
 */
function scoreCelebration(score: number): { Icon: LucideIcon; color: string } | null {
  if (score >= 100) return { Icon: PartyPopper, color: 'hsl(var(--fb-lime))' };
  if (score >= 90) return { Icon: Sparkles, color: 'hsl(var(--fb-amber))' };
  if (score >= 70) return { Icon: Award, color: 'hsl(var(--success))' };
  return null;
}

type AssetDrawerState =
  | { kind: 'none' }
  | { kind: 'list' }
  | { kind: 'detail'; asset: Asset };

function assetDrawerLevel(state: AssetDrawerState): number {
  switch (state.kind) {
    case 'none':
      return 0;
    case 'list':
      return 1;
    case 'detail':
      return 2;
  }
}

export const Route = createFileRoute('/learn/courses/$courseId/')({
  component: CourseDetailPage,
});

function CourseDetailPage() {
  const { courseId } = Route.useParams();
  const navigate = useNavigate();

  const { data: course, isLoading, error } = useCourse(courseId);
  const enrollInCourse = useEnrollInCourse();
  const markLessonStarted = useMarkLessonStarted();

  const visibleLinkedAssets = useMemo(
    () => (course?.linked_assets ?? []).filter(hasAssetSource),
    [course?.linked_assets],
  );

  const [assetDrawer, setAssetDrawer] = useState<AssetDrawerState>({ kind: 'none' });
  const openCourseAssets = useCallback(() => setAssetDrawer({ kind: 'list' }), []);
  const openAssetDetail = useCallback(
    (asset: Asset) => setAssetDrawer({ kind: 'detail', asset }),
    [],
  );
  const backToList = useCallback(() => setAssetDrawer({ kind: 'list' }), []);
  const closeAssetDrawer = useCallback(() => setAssetDrawer({ kind: 'none' }), []);
  const popAssetDrawer = useCallback(() => {
    setAssetDrawer((current) => {
      if (current.kind === 'detail') return { kind: 'list' };
      if (current.kind === 'list') return { kind: 'none' };
      return current;
    });
  }, []);
  useDrawerHistory(assetDrawerLevel(assetDrawer), popAssetDrawer);

  // Fetch user's enrollment and lesson progress for this course
  const { data: enrollment } = useCourseEnrollment(courseId);
  const { data: lessonProgressData = [] } = useLessonProgress(courseId);
  const { data: bestScore } = useBestAssessmentScore(courseId);

  const isCoursePassed = enrollment?.status === 'completed';

  // Prime browser HTTP cache so opening a lesson feels instant.
  // - useCoursePrefetch: tier 1 (manifest + variant playlist + poster) for
  //   every lesson in this course that has finished transcoding.
  // - useInProgressCoursesPrefetch: thumbnails + lesson posters for the
  //   user's other in-progress courses, lowest priority. Excludes this one.
  useCoursePrefetch(courseId);
  useInProgressCoursesPrefetch(courseId);

  // Track course view
  useTrackEntityView({
    entityType: 'course',
    entityId: courseId,
    entityName: course?.name || 'Untitled Course',
    metadata: {
      totalModules: course?.modules?.length || 0,
      totalLessons: course?.modules?.reduce((sum, m) => sum + (m.lessons?.length || 0), 0) || 0,
      category: course?.pillar,
      estimatedHours: course?.estimated_hours,
    },
  });

  // Build completed lessons set from lesson progress data
  const completedLessonIds = new Set(
    lessonProgressData
      .filter((lp) => lp.status === 'completed')
      .map((lp) => lp.lesson_id)
  );

  // Convert to array for compatibility with existing code
  const completedLessons = Array.from(completedLessonIds);
  const currentLessonId = enrollment?.current_lesson_id ?? undefined;

  // Derive the effective current lesson: DB value, or first incomplete lesson for enrolled users
  const effectiveCurrentLessonId = useMemo(() => {
    if (currentLessonId) return currentLessonId;
    if (!enrollment || !course?.modules) return undefined;
    // Find first incomplete lesson (same logic as handleContinue)
    for (const mod of [...(course.modules || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))) {
      for (const lesson of [...(mod.lessons || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))) {
        if (!completedLessonIds.has(lesson.id)) return lesson.id;
      }
    }
    return undefined;
  }, [currentLessonId, enrollment, course?.modules, completedLessonIds]);

  // Resolve poster from lesson media (current lesson for enrolled, random otherwise)
  const heroLessonPoster = useMemo(() => {
    const postersWithMedia = (course?.lesson_posters ?? []).filter(
      (lp) => lp.video_media?.poster_url || lp.video_media?.small_poster_url,
    );

    if (effectiveCurrentLessonId) {
      const current = postersWithMedia.find((lp) => lp.id === effectiveCurrentLessonId);
      if (current?.video_media) return current.video_media;
    }

    if (postersWithMedia.length === 0) return null;
    const randomIdx = Math.floor(Math.random() * postersWithMedia.length);
    return postersWithMedia[randomIdx].video_media;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCurrentLessonId, course?.id]);

  // Calculate total lessons and progress
  const modules = course?.modules || [];

  // Completion drip (e.g. Basecamp): module N+1 stays locked until module N's
  // lessons are completed. null = drip off, everything unlocked.
  // Drip is the free-preview pacer, so it applies only to users WITHOUT content
  // entitlement — the same gate `useDripSeries` applies to the series chain below.
  // A paying member gets "the complete Basecamp course", modules included.
  // super_admin sees the whole curriculum — drip pacing (module + series) doesn't apply.
  const { isSuperAdmin } = useIsSuperAdmin();
  const { data: hasEntitlement } = useHasContentEntitlement();
  // A course given free through the person's sign-up link opens whole — the
  // link is the offer. (The series chain needs no exception: RLS shows a lead
  // only their own link course, so its series is that one course.)
  const linkCourseId = useMyLinkCourseId();
  const dripActive =
    course?.drip_mode === 'completion' && !isSuperAdmin && hasEntitlement === false &&
    linkCourseId !== courseId;

  // Course-SERIES drip: Basecamp ships as flat sibling courses (BC00→BC12),
  // so the chain also runs across courses sharing the access level.
  const { data: dripSeries } = useDripSeries(course);
  const seriesLocked = !!dripSeries && dripSeries.courses.length > 1 &&
    !dripSeries.unlockedIds.has(courseId);
  const seriesPrevious = seriesLocked
    ? (dripSeries.previousOf.get(courseId) ?? null)
    : null;
  const unlockedModuleIds = useMemo(
    () => (dripActive ? computeUnlockedModules(modules, completedLessonIds) : null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dripActive, modules, completedLessons.join(',')],
  );
  const isModuleLocked = (moduleId: string) =>
    !!unlockedModuleIds && !unlockedModuleIds.has(moduleId);
  const totalLessons = modules.reduce((sum, m) => sum + (m.lessons?.length || 0), 0);
  const completedCount = completedLessons.length;

  // DERIVED, never read from course_enrollments.progress_percentage.
  //
  // That column is a write-once denormalisation: its trigger fires only on the
  // `→ completed` and `not_started → in_progress` transitions of THIS user's
  // lesson_progress, so nothing recomputes it when a course gains lessons, and
  // nothing recomputes it on later writes to an already-in_progress row. It
  // drifts and can never self-correct.
  //
  // Reading it here is what made this page contradict itself: the wheel and bar
  // rendered the stored 50% while the captions beside them — derived from the
  // same lesson_progress rows used below — said "1 of 14 lessons completed"
  // (7%), and ModuleAccordion, which also derives, agreed with the captions.
  // One source means the four renderings cannot disagree again.
  //
  // The column is still maintained for analytics/posterity (nine consumers read
  // it, incl. gamification, badge-calculator and Alfie's user-context) — it just
  // no longer decides what a member sees.
  const progress = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0;
  // Every lesson done but the course not completed: passing the course assessment is the
  // last step (hermes migrations 287, 689), so the main button points there.
  const pendingAssessment =
    progress === 100 && !isCoursePassed ? (course?.assessments ?? [])[0] : undefined;

  // Calculate total duration
  const totalDuration = modules.reduce((sum, m) => {
    return sum + (m.lessons?.reduce((lSum, l) => lSum + (l.duration_minutes || 0), 0) || 0);
  }, 0);

  const handleLessonClick = (lesson: Lesson) => {
    navigate({
      to: '/learn/courses/$courseId/lessons/$lessonId',
      params: { courseId, lessonId: lesson.id },
    });
  };

  const handleContinue = async () => {
    // Enroll user in course first if not already enrolled
    try {
      await enrollInCourse.mutateAsync(courseId);
    } catch (error) {
      console.error('Failed to enroll in course:', error);
      // Continue anyway - they may already be enrolled
    }

    // Find first incomplete lesson (drip: never target a locked module's lesson)
    let targetLesson: Lesson | null = null;
    for (const module of modules) {
      if (isModuleLocked(module.id)) continue;
      for (const lesson of module.lessons || []) {
        if (!completedLessons.includes(lesson.id)) {
          targetLesson = lesson;
          break;
        }
      }
      if (targetLesson) break;
    }

    // If no incomplete lessons found, go to first lesson for review
    if (!targetLesson) {
      targetLesson = modules[0]?.lessons?.[0] || null;
    }

    // Auto-start the lesson if it hasn't been started yet
    if (targetLesson) {
      const lessonProgress = lessonProgressData.find((lp) => lp.lesson_id === targetLesson!.id);
      if (!lessonProgress || lessonProgress.status === 'not_started') {
        try {
          await markLessonStarted.mutateAsync({
            lessonId: targetLesson.id,
            courseId,
          });
        } catch (error) {
          console.error('Failed to mark lesson as started:', error);
          // Continue anyway
        }
      }
      handleLessonClick(targetLesson);
    }
  };

  if (isLoading) {
    return (
      <div className="w-full max-w-5xl mx-auto">
        {/* Back link — renders immediately */}
        <Link
          to="/learn"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Learning
        </Link>

        {/* Course header skeleton */}
        <div className="bg-card rounded-xl border border-border overflow-hidden mb-6">
          <div className="md:flex">
            <Skeleton className="md:w-64 h-48 flex-shrink-0" />
            <div className="flex-1 p-6 space-y-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-7 w-3/4" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-2/3" />
              <div className="flex gap-4 pt-2">
                <Skeleton className="h-5 w-20" />
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-5 w-16" />
              </div>
              <Skeleton className="h-10 w-40 rounded-xl mt-4" />
            </div>
          </div>
        </div>

        {/* Content skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 min-w-0 space-y-3">
            <Skeleton className="h-6 w-40 mb-4" />
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
            <Skeleton className="h-16 w-full rounded-xl" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-48 w-full rounded-xl" />
            <Skeleton className="h-40 w-full rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (error || !course) {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <Link
          to="/learn"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Learning
        </Link>
        <div className="bg-card rounded-xl border border-border p-8 text-center">
          <h2 className="text-xl font-semibold text-foreground mb-2">Course Not Found</h2>
          <p className="text-muted-foreground">
            The course you're looking for doesn't exist or has been removed.
          </p>
        </div>
      </div>
    );
  }

  // Series drip: this course is later in its chain — point at the gatekeeper.
  if (seriesLocked) {
    return (
      <div className="w-full max-w-5xl mx-auto">
        <Link
          to="/learn"
          className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Learning
        </Link>
        <div className="bg-card rounded-xl border border-border p-10 text-center max-w-xl mx-auto">
          <div className="w-14 h-14 rounded-full bg-secondary flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7 text-muted-foreground" aria-hidden />
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-2">
            This course is still locked
          </h2>
          <p className="text-muted-foreground mb-6">
            {seriesPrevious
              ? <>Complete <span className="font-medium text-foreground">{seriesPrevious.name}</span> to unlock it.</>
              : 'Complete the previous course in the series to unlock it.'}
          </p>
          {seriesPrevious && (
            <Link
              to="/learn/courses/$courseId"
              params={{ courseId: seriesPrevious.id }}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all"
            >
              <Play className="w-4 h-4" />
              Go to {seriesPrevious.name}
            </Link>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Back navigation */}
      <Link
        to="/learn"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Learning
      </Link>

      {/* Course Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card rounded-xl border border-border overflow-hidden mb-6"
      >
        <div className="md:flex">
          {/* Thumbnail */}
          <div className="md:w-64 flex-shrink-0 overflow-hidden bg-black flex items-center">
            <MediaImage
              src={resolveMediaUrl(
                heroLessonPoster?.small_poster_url ?? heroLessonPoster?.poster_url
                ?? course.thumbnail_media?.small_poster_url
                ?? course.thumbnail_media?.poster_url
                ?? course.thumbnail_url
              )}
              blurhash={heroLessonPoster?.blurhash ?? course.thumbnail_media?.blurhash}
              alt={course.name}
              className="w-full h-48"
              fallback={<BookOpen className="w-16 h-16 text-white/80 m-auto" />}
            />
          </div>

          {/* Content */}
          <div className="flex-1 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                {course.pillar && (
                  <span className="text-xs text-muted-foreground mb-1 block">
                    {course.pillar}
                  </span>
                )}
                <div className="flex items-center gap-3">
                  <h1 className="text-2xl font-bold text-foreground">{course.name}</h1>
                  {bestScore !== null && bestScore !== undefined && (
                    <span className="flex items-center gap-1.5 text-base font-serif italic text-primary">
                      {(() => {
                        const celebration = scoreCelebration(bestScore);
                        return celebration ? (
                          <celebration.Icon
                            className="w-4 h-4 shrink-0"
                            style={{ color: celebration.color }}
                          />
                        ) : null;
                      })()}
                      You scored: {bestScore}%
                    </span>
                  )}
                </div>
              </div>
              {isCoursePassed && (
                <div className="bg-success/15 text-success px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1">
                  <CheckCircle2 className="w-4 h-4" />
                  Complete
                </div>
              )}
            </div>

            <p className="text-muted-foreground mb-6">
              {course.description || 'No description available'}
            </p>

            {/* Stats */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-6">
              <div className="flex items-center gap-1.5">
                <Layers className="w-4 h-4" />
                <span>{modules.length} module{modules.length !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Play className="w-4 h-4" />
                <span>{totalLessons} lesson{totalLessons !== 1 ? 's' : ''}</span>
              </div>
              {visibleLinkedAssets.length > 0 && (
                <button
                  type="button"
                  onClick={openCourseAssets}
                  className="flex items-center gap-1.5 text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors"
                >
                  <NavIconLibrary className="w-4 h-4" />
                  <span>
                    {visibleLinkedAssets.length} asset{visibleLinkedAssets.length === 1 ? '' : 's'}
                  </span>
                </button>
              )}
              {totalDuration > 0 && (
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  <span>
                    {totalDuration >= 60
                      ? `${Math.floor(totalDuration / 60)}h ${totalDuration % 60}m`
                      : `${totalDuration}m`}
                  </span>
                </div>
              )}
              {course.estimated_hours && (
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  <span>~{course.estimated_hours} hours</span>
                </div>
              )}
            </div>

            {/* Progress bar */}
            {progress > 0 && (
              <div className="mb-6">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-muted-foreground">Your Progress</span>
                  <span className="font-semibold text-foreground">
                    {completedCount} of {totalLessons} lessons
                  </span>
                </div>
                <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-purple-500 to-indigo-600"
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 0.8, ease: 'easeOut' }}
                  />
                </div>
              </div>
            )}

            {/* Action button */}
            {pendingAssessment ? (
              <Link
                to="/learn/assessments/$assessmentId"
                params={{ assessmentId: pendingAssessment.id }}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all inline-flex items-center gap-2"
              >
                <ClipboardCheck className="w-4 h-4" />
                Take the assessment to finish this course
              </Link>
            ) : (
              <button
                onClick={handleContinue}
                className="px-6 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Play className="w-4 h-4" />
                {progress === 0
                  ? 'Start Course'
                  : progress === 100
                  ? 'Review Course'
                  : 'Continue Learning'}
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Progress Overview (side panel on larger screens) */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Modules list */}
        <div className="md:col-span-2 min-w-0">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h2 className="text-2xl font-semibold text-foreground mb-4">Course Content</h2>

            {modules.length > 0 ? (
              <div className="space-y-3">
                {modules
                  .sort((a, b) => a.order - b.order)
                  .map((module, idx) => (
                    <ModuleAccordion
                      key={module.id}
                      module={module}
                      index={idx}
                      completedLessons={completedLessons}
                      currentLessonId={effectiveCurrentLessonId}
                      isExpanded={idx === 0} // First module expanded by default
                      isLocked={isModuleLocked(module.id)}
                      lockedHint="Complete the previous module to unlock"
                      onLessonClick={handleLessonClick}
                    />
                  ))}
              </div>
            ) : (
              <div className="bg-card rounded-xl border border-border p-8 text-center">
                <Layers className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">
                  No modules have been added to this course yet.
                </p>
              </div>
            )}

            {/* Course-level assessments (not inside any module) */}
            {(course.assessments ?? []).length > 0 && (
              <div className="mt-6">
                <h3 className="text-md font-semibold text-foreground mb-3">Assessments</h3>
                <div className="bg-card rounded-xl border border-border overflow-hidden">
                  <div className="px-4 py-2 space-y-1">
                    {course.assessments!.map((assessment) => (
                      <Link
                        key={assessment.id}
                        to="/learn/assessments/$assessmentId"
                        params={{ assessmentId: assessment.id }}
                        className="w-full px-3 py-2.5 flex items-center gap-3 rounded-lg text-left transition-colors hover:bg-secondary/50"
                      >
                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-amber-500/20 text-amber-500">
                          <ClipboardCheck className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {assessment.name}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {assessment.type === 'quiz' ? 'Quiz' : assessment.type === 'exam' ? 'Exam' : assessment.type === 'practice' ? 'Practice' : 'Survey'}
                            {assessment.passing_score ? ` · ${assessment.passing_score}% to pass` : ''}
                            {assessment.time_limit ? ` · ${assessment.time_limit} min` : ''}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Progress card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-card rounded-xl border border-border p-5"
          >
            <h3 className="text-sm font-medium text-muted-foreground mb-4">Course Progress</h3>
            <div className="flex items-center justify-center mb-4">
              <ProgressRing progress={progress} size={100} strokeWidth={8} />
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-foreground">{progress}%</p>
              <p className="text-sm text-muted-foreground">
                {completedCount} of {totalLessons} lessons completed
              </p>
            </div>
          </motion.div>

          {/* Course info card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-card rounded-xl border border-border p-5"
          >
            <h3 className="text-sm font-medium text-muted-foreground mb-4">About this course</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 text-muted-foreground">
                <Layers className="w-4 h-4 flex-shrink-0" />
                <span>{modules.length} modules</span>
              </div>
              <div className="flex items-center gap-2 text-muted-foreground">
                <Play className="w-4 h-4 flex-shrink-0" />
                <span>{totalLessons} lessons</span>
              </div>
              {visibleLinkedAssets.length > 0 && (
                <button
                  type="button"
                  onClick={openCourseAssets}
                  className="flex items-center gap-2 text-sky-600 dark:text-sky-400 hover:text-sky-700 dark:hover:text-sky-300 transition-colors w-full text-left"
                >
                  <NavIconLibrary className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {visibleLinkedAssets.length} course asset
                    {visibleLinkedAssets.length === 1 ? '' : 's'}
                  </span>
                </button>
              )}
              {totalDuration > 0 && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Clock className="w-4 h-4 flex-shrink-0" />
                  <span>
                    {totalDuration >= 60
                      ? `${Math.floor(totalDuration / 60)}h ${totalDuration % 60}m total`
                      : `${totalDuration} minutes total`}
                  </span>
                </div>
              )}
              {isCoursePassed ? (
                <Link
                  to="/learn/certificates"
                  className="flex items-center gap-2 text-green-400 hover:text-green-300 transition-colors"
                >
                  <Award className="w-4 h-4 flex-shrink-0" />
                  <span>View Certificate</span>
                </Link>
              ) : (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Award className="w-4 h-4 flex-shrink-0" />
                  <span>Certificate on completion</span>
                </div>
              )}
              {bestScore !== null && bestScore !== undefined && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <ClipboardCheck className="w-4 h-4 flex-shrink-0" />
                  <span>Assessment: <span className="font-semibold text-foreground">{bestScore}%</span></span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Next lesson card */}
          {progress > 0 && !isCoursePassed && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-gradient-to-br from-indigo-500/20 to-purple-500/20 rounded-xl border border-indigo-500/30 p-5"
            >
              <h3 className="text-sm font-medium text-foreground mb-2">Continue where you left off</h3>
              <button
                onClick={handleContinue}
                className="w-full mt-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                <Play className="w-4 h-4" />
                Resume
              </button>
            </motion.div>
          )}
        </div>
      </div>

      {/* Course Assets drawers — sequential: only one is visible at a time. */}
      <CourseAssetsDrawer
        assets={visibleLinkedAssets}
        isOpen={assetDrawer.kind === 'list'}
        onClose={closeAssetDrawer}
        onAssetClick={openAssetDetail}
      />
      <AssetDetailPanel
        asset={assetDrawer.kind === 'detail' ? assetDrawer.asset : null}
        isOpen={assetDrawer.kind === 'detail'}
        onClose={closeAssetDrawer}
        onBack={backToList}
        backLabel="Back to course assets"
      />
    </div>
  );
}
